import { TableRow } from "@mui/material";
import { DataTableCell } from "../data-table-cell";
import { DataTableCellCheckbox } from "../data-table-cell-checkbox";
import { formatDataTableRow, formatNullCellValue } from "../utils";
import { DataTableRowProps } from "../types";
import { memo, useCallback, useRef, useState } from "react";
import { TableRowLoadingEffect } from "./loading-effect";

export const DataTableRow: React.FC<DataTableRowProps> = memo(
  ({ data, onSelect, selected, disabled, formatCell, hiddenColumns }) => {
    const [rowWidth, setRowWidth] = useState(400);

    const hasSetWidth = useRef(false);
    const handleRowWidth = useCallback((ref: HTMLTableRowElement) => {
      if (ref && !hasSetWidth.current) {
        hasSetWidth.current = true;
        setRowWidth(ref.getBoundingClientRect().width);
      }
    }, []);
    const isSelectable = !!onSelect;
    const formattedCells = formatDataTableRow(data, formatCell);
    return (
      <TableRow
        ref={handleRowWidth}
        sx={{
          opacity: disabled ? 0.5 : 1,
          position: "relative",
          background:
            !disabled && selected
              ? (theme) => theme.palette.action.selected
              : "transparent",
          "&:hover": {
            background: (theme) =>
              selected
                ? theme.palette.action.selected
                : theme.palette.action.hover,
          },
        }}
      >
        {isSelectable && (
          <DataTableCellCheckbox
            shrink
            disabled={disabled}
            checked={selected}
            onChange={onSelect}
          />
        )}

        {Object.keys(formattedCells).map((key) =>
          hiddenColumns.includes(key) ? null : (
            <DataTableCell key={key}>
              {formatNullCellValue(formattedCells?.[key])}
            </DataTableCell>
          )
        )}

        {hasSetWidth.current && disabled && (
          <TableRowLoadingEffect visible={!!disabled} rowWidth={rowWidth} />
        )}
      </TableRow>
    );
  },
  (prevProps, nextProps) => {
    try {
      return (
        !!prevProps.onSelect === !!nextProps.onSelect &&
        prevProps.selected === nextProps.selected &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.hiddenColumns.join(",") === nextProps.hiddenColumns.join(",") &&
        JSON.stringify(prevProps?.data || {}) ===
          JSON.stringify(nextProps?.data || {})
      );
    } catch (error) {
      // If JSON stringify fails, do shallow comparison instead
      if (
        !!prevProps.onSelect !== !!nextProps.onSelect ||
        prevProps.selected !== nextProps.selected ||
        prevProps.disabled !== nextProps.disabled ||
        prevProps.hiddenColumns.join(",") !== nextProps.hiddenColumns.join(",")
      ) {
        return false;
      }
      
      const prevDataKeys = Object.keys(prevProps?.data || {});
      const nextDataKeys = Object.keys(nextProps?.data || {});
      
      if (prevDataKeys.length !== nextDataKeys.length) {
        return false;
      }
      
      return prevDataKeys.every(
        (key) => prevProps.data[key] === nextProps.data[key]
      );
    }
  }
);
